import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Text from "../../components/common/Text";
import { urlbase, headers } from "../../constants/Constants";
import LoadingBox from "../Partials/LoadingBox";
import { getTestHistory } from "../../api/documents";
import { TextField } from "@mui/material";

interface TestInfo {
  id: number;
  name: string;
  public: number;
  hints: number;
  grade: string;
  timealloc: number;
  version: number;
  subject_id: number;
  area_id: number;
  module_id: number;
  section_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
  is_survey: number;
  school_id: number;
  competition: number;
  question_randomize: number;
  distractor_randomize: number;
  pdf_url: null | string;
}

interface TestItem {
  test_id: number;
  group_test_id: number;
  test_info: TestInfo;
  close_date: null | string;
  end_date: string | null;
  date_completed?: string;
  is_assigned: boolean;
  result?: number;
  total_marks?: number | null;
  start_time?: string;
  group_id?: number;
}

interface TestData {
  regularTests: TestItem[];
  assignedTests: TestItem[];
}

interface TestHistoryProps {
  user: any;
  tests?: TestData;
}

export default function TestHistory(props: TestHistoryProps) {
  const history = useHistory();
  if (props === undefined || props.user === undefined) history.push("/login");
  const user = props.user;
  const [historydata, setHistorydata] = useState<TestData>({ regularTests: [], assignedTests: [] });
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // Use the provided tests data or the fetched history data
  const testsData = props.tests || historydata;
  
  // Combine both regular and assigned tests for search functionality
  let listToDisplay: TestItem[] = [];
  if (testsData) {
    const allTests = [...(testsData.regularTests || []), ...(testsData.assignedTests || [])];
    
    // Apply search filter
    listToDisplay = allTests.filter((item) => {
      if (item.test_info !== null) {
        return item.test_info.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      }
      return false;
    });
  }
  
  // Use effect
  useEffect(() => {
    if (!props.tests) {
      getHistorydata();
    }
  }, [props.tests]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const renderHistoryList = () => {
    // Split tests into assigned tests and regular tests
    const assignedTests = listToDisplay
      .filter(item => item.is_assigned === true)
      .sort((a, b) => {
        // Handle cases where startTime might be undefined
        if (!a.start_time) return 1;
        if (!b.start_time) return -1;
        return a.start_time < b.start_time ? 1 : -1;
      });
    
    const regularTests = listToDisplay
      .filter(item => item.is_assigned !== true)
      .sort((a, b) => {
        // Handle cases where startTime might be undefined
        if (!a.start_time) return 1;
        if (!b.start_time) return -1;
        return a.start_time < b.start_time ? 1 : -1;
      });

    return (
      <>
        {assignedTests.length > 0 && (
          <>
            <div style={{ 
              padding: "10px", 
              backgroundColor: "#00122D", 
              color: "#FBCD02", 
              fontWeight: "bold",
              borderRadius: "5px",
              marginBottom: "10px"
            }}>
              Assigned Tests
            </div>
            {assignedTests.map((item, i) => renderTestItem(item, i))}
          </>
        )}

        {regularTests.length > 0 && (
          <>
            <div style={{ 
              padding: "10px", 
              backgroundColor: "#00122D", 
              color: "#FBCD02", 
              fontWeight: "bold",
              borderRadius: "5px",
              marginTop: "15px",
              marginBottom: "10px"
            }}>
              Tests
            </div>
            {regularTests.map((item, i) => renderTestItem(item, i))}
          </>
        )}
      </>
    );
  };

  const renderTestItem = (item: TestItem, i: number) => {
    const isClickable = isTestClickable(item);
    // Check if test is not yet available (future test)
    const isFutureTest = item.end_date && new Date() < new Date(item.end_date);
    
    return (
      <div
        style={{
          padding: "10px",
          cursor: isClickable ? "pointer" : "default",
          marginBottom: "5px",
          backgroundColor: "#DFDFDF",
          border: isClickable ? "1px solid #FBCD02" : "1px solid transparent",
        }}
        key={i}
        onClick={
          isClickable 
            ? () => {
                history.push("/memo", { user: user, test: item });
              }
            : undefined
        }
        className={
          isClickable 
            ? "AssignedTestHist" 
            : "TestHist"
        }
      >
        <div style={{ width: "100%", display: "flex", padding: "5px" }}>
          <div
            style={{
              color: "grey",
              width: "100%",
              textAlign: "left",
              flex: 3,
            }}
          >
            {item.start_time ? getFormattedDate(item.start_time) : item.date_completed ? getFormattedDate(item.date_completed) : "N/A"}
          </div>
          <div
            style={{
              textAlign: "right",
              marginRight: "20px",
              flex: 3,
              color: "#6B7787",
            }}
          >
            {item.result !== undefined ? `${item.result}%` : "N/A"}
          </div>
        </div>
        <div>
          <div
            style={{
              maxWidth: "85%",
              fontWeight: "bold",
              padding: "5px",
            }}
          >
            {item.test_info.name}
          </div>
          {isFutureTest && (
            <div
              style={{
                color: "#D32F2F",
                fontSize: "12px",
                padding: "5px",
                fontStyle: "italic"
              }}
            >
              Available from: {item.end_date ? getFormattedDate(item.end_date) : "N/A"}
            </div>
          )}
        </div>
      </div>
    );
  };
  // Check if a test is clickable based on dates
  const isTestClickable = (item: TestItem) => {
    const currentDate = new Date();

    // if the test is not assigned, it is not clickable
    if (item.is_assigned === false) {
      return false;
    }
    
    // If there is no end date, the test is always clickable
    if (!item.end_date) {
      return true;
    }
    
    const endDate = new Date(item.end_date);
    
    // If there is no close date, the test is clickable after the end date
    if (item.close_date === null) {
      return currentDate >= endDate;
    }
    
    // If both dates exist, test is clickable if the current date is between end date and close date
    const closeDate = new Date(item.close_date);
    if (item.test_id === 953) {
      console.log("closeDate", closeDate);
      console.log("endDate", endDate);
      console.log("currentDate", currentDate);
      console.log("currentDate >= endDate", currentDate >= endDate);
      console.log("currentDate <= closeDate", currentDate <= closeDate);
    }
    return currentDate >= endDate && currentDate <= closeDate;
  };
  function getFormattedDate(input: string | null | undefined) {
    if (!input) return "N/A";
    
    try {
      // Create a date object to standardize the input
      const date = new Date(input);
      
      // Check if date is valid
      if (isNaN(date.getTime())) return input;

      const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const day = date.getDate();
      const month = date.getMonth(); // 0-based
      const year = date.getFullYear();
      
      var months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      return day + " " + months[month] + " " + year + " " + time;
    } catch (error) {
      // If any error in parsing, return the original input
      return input;
    }
  }
  const getHistorydata = async () => {
    setIsLoading(true);
    const historyResponse = await getTestHistory(user.token);
    if (historyResponse.code == 200) {
      setIsLoading(false);
      setHistorydata(historyResponse.data);
    } else {
      return <Text color={"#999999"}>Unable to find any history items.</Text>;
    }
  };
  const inputSelectStyle: React.CSSProperties = {
    backgroundColor: "#00122D",
    borderRadius: "5px",
    fontSize: "0.875rem",
    color: "white",
    fontWeight: "bold",
    border: "1px solid #FBCD02",
    width: "100%",
    padding: "15px 14px",
    boxSizing: "border-box",
    //marginBottom: "10px",
  };
  return (
    // App container
    <div
      style={{
        maxWidth: "100%",
        // maxHeight: "1600px",
        backgroundColor: "#212A41",
        padding: "20px",
        borderRadius: "5px",
      }}
    >
      {/* History container */}
      <div
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
        }}
      >
        <div>
          {/* Search function */}
          <TextField
            label="Search"
            id="outlined-basic"
            variant="outlined"
            // placeholder="Search"
            value={searchTerm}
            onChange={handleChange}
            fullWidth
            sx={{
              backgroundColor: "#00122d",
              borderRadius: "5px",
              marginBottom: "10px",
              width: "100%",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#00122d",
                },
                "&:hover fieldset": {
                  borderColor: "#FBCD02",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FBCD02",
                },
              },
            }}
            InputLabelProps={{
              style: { color: "#FBCD02" },
            }}
            inputProps={{
              style: { color: "white" },
            }}
          />
        </div>
        {/* Container */}
        <div style={{ backgroundColor: "#212A41" }}>
          <LoadingBox display={isLoading} />
          {/* Content container */}
          <div
            style={{
              // backgroundColor: "#DFDFDF",
              height: "100%",
              borderRadius: "5px",
            }}
          >
            {/* {regularPrint()} */}
            <table style={{ width: "100%" }}>{renderHistoryList()}</table>
          </div>
        </div>
      </div>
    </div>
  );
}
